import {CommonModule} from '@angular/common';
import {Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {ActivitiesSearchEngineModule} from '@modules/activities/core/activities-search-engine';
import {
    SearchFilterSelection,
    SearchFilterValue,
} from 'shared/search-engine';
import {DataEntity} from 'octopus-connect';
import {MathiaSearchDataService} from '../mathia-search-data.service';
import {MathiaLessonsSearchFilterComponent} from '../mathia-lessons-search-filter/mathia-lessons-search-filter.component';

@Component({
    selector: 'app-mathia-activity-search-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        ActivitiesSearchEngineModule,
        MathiaLessonsSearchFilterComponent,
        TranslateModule,
    ],
    templateUrl: './mathia-activity-search-dialog.component.html',
})
export class MathiaActivitySearchDialogComponent implements OnInit, OnDestroy {
    private readonly dialogRef = inject(MatDialogRef<MathiaActivitySearchDialogComponent>);
    private readonly searchData = inject(MathiaSearchDataService);

    @ViewChild('dialogContent', {read: ElementRef, static: true})
    private readonly dialogContentElement!: ElementRef<HTMLElement>;

    @ViewChild('resultsSentinel', {read: ElementRef})
    private set resultsSentinelElement(element: ElementRef<HTMLElement> | undefined) {
        this.sentinelElement = element?.nativeElement;
        this.observeResultsSentinel();
    }

    public selection: SearchFilterSelection<SearchFilterValue> = {};
    public activities: DataEntity[] = [];
    public isLoading = true;
    public hasNextPage = true;
    public hasLoadError = false;
    private selectedActivityIds = new Set<string | number>();
    private searchVersion = 0;
    private currentPage = 0;
    private searchSubscription?: Subscription;
    private sentinelElement?: HTMLElement;
    private intersectionObserver?: IntersectionObserver;

    public ngOnInit(): void {
        this.startSearch();
    }

    public ngOnDestroy(): void {
        this.searchSubscription?.unsubscribe();
        this.intersectionObserver?.disconnect();
    }

    public onSelectionChange(selection: SearchFilterSelection<SearchFilterValue>): void {
        this.selection = {...selection};
        this.selectedActivityIds.clear();
        this.startSearch();
    }

    public toggleActivity(id: string | number): void {
        if (this.selectedActivityIds.has(id)) {
            this.selectedActivityIds.delete(id);
        } else {
            this.selectedActivityIds.add(id);
        }
    }

    public isSelected(id: string | number): boolean {
        return this.selectedActivityIds.has(id);
    }

    public get selectedActivityCount(): number {
        return this.selectedActivityIds.size;
    }

    public clearSelection(): void {
        this.selectedActivityIds.clear();
    }

    public close(): void {
        this.dialogRef.close([...this.selectedActivityIds]);
    }

    public retryLoading(): void {
        this.hasLoadError = false;
        this.loadNextPage();
    }

    private startSearch(): void {
        this.searchSubscription?.unsubscribe();
        this.activities = [];
        this.currentPage = 0;
        this.hasNextPage = true;
        this.hasLoadError = false;
        this.isLoading = false;
        this.loadNextPage();
    }

    private loadNextPage(): void {
        if (this.isLoading || !this.hasNextPage || this.hasLoadError) {
            return;
        }

        this.isLoading = true;
        const page = this.currentPage + 1;
        const searchVersion = ++this.searchVersion;
        this.searchSubscription = this.searchData.searchActivities(this.selection, page).subscribe({
            next: result => {
                if (searchVersion !== this.searchVersion) {
                    return;
                }

                this.activities = [...this.activities, ...result.activities];
                this.currentPage = page;
                this.hasNextPage = result.hasNextPage;
                this.isLoading = false;
                this.observeResultsSentinel();
            },
            error: () => {
                if (searchVersion !== this.searchVersion) {
                    return;
                }

                this.hasLoadError = true;
                this.isLoading = false;
            },
        });
    }

    private observeResultsSentinel(): void {
        this.intersectionObserver?.disconnect();

        if (!this.sentinelElement || !this.dialogContentElement) {
            return;
        }

        this.intersectionObserver = new IntersectionObserver(
            entries => {
                if (entries.some(entry => entry.isIntersecting)) {
                    this.loadNextPage();
                }
            },
            {
                root: this.dialogContentElement.nativeElement,
                rootMargin: '0px 0px 200px 0px',
            },
        );
        this.intersectionObserver.observe(this.sentinelElement);
    }
}
